import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Options for formatCurrency.
 * - decimals: number of fraction digits (default 2). Pass 0 for whole-dollar display ($1,234).
 * - includeSymbol: prefix with $. Default true.
 */
export interface FormatCurrencyOptions {
  decimals?: number;
  includeSymbol?: boolean;
}

/**
 * Format a number as US currency with proper thousands separators and decimal places.
 *
 * Backwards-compatible: the second arg can be a boolean (legacy `includeSymbol`) or an options object.
 *
 * @example
 * formatCurrency(10800)                       // "$10,800.00"
 * formatCurrency(10800, false)                // "10,800.00"           (legacy form)
 * formatCurrency(10800, { decimals: 0 })      // "$10,800"
 * formatCurrency(10800, { decimals: 0, includeSymbol: false }) // "10,800"
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  optionsOrIncludeSymbol: boolean | FormatCurrencyOptions = true
): string {
  // Normalize args (preserve old `(amount, false)` signature)
  const options: FormatCurrencyOptions =
    typeof optionsOrIncludeSymbol === 'boolean'
      ? { includeSymbol: optionsOrIncludeSymbol }
      : optionsOrIncludeSymbol;
  const decimals = options.decimals ?? 2;
  const includeSymbol = options.includeSymbol ?? true;

  try {
    let numAmount: number;

    if (amount === null || amount === undefined) {
      numAmount = 0;
    } else if (typeof amount === 'string') {
      const cleanedAmount = amount.replace(/[$,\s]/g, '');
      numAmount = parseFloat(cleanedAmount) || 0;
    } else if (typeof amount === 'number') {
      numAmount = amount;
    } else {
      numAmount = 0;
    }

    if (isNaN(numAmount)) numAmount = 0;

    const formatted = numAmount.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

    return includeSymbol ? `$${formatted}` : formatted;
  } catch (error) {
    console.error('Error in formatCurrency:', error, 'Input:', amount);
    return includeSymbol ? (decimals === 0 ? '$0' : '$0.00') : (decimals === 0 ? '0' : '0.00');
  }
}
